// ---------------------------------------------------------------------------
// AddressValidationStep — Confirm geocoded location or enter manually
// ---------------------------------------------------------------------------
// Three render paths:
//   1. **Geocoding succeeded, high confidence** → Predefined, widget never
//      sees this step (auto-confirmed by backend or auto-submitted).
//   2. **Geocoding succeeded, low confidence** → Confirmation card with map,
//      original address vs detected location. User confirms, goes back, or
//      enters coordinates manually.
//   3. **Geocoding failed** → Warning banner + go-back link + manual
//      lat/lon inputs.
//
// Submit payloads:
//   - Confirm:   { confirmed: true }
//   - Override:   { confirmed: true, coordinates: { latitude, longitude } }
//   - Manual:     { confirmed: true, coordinates: { latitude, longitude } }
// ---------------------------------------------------------------------------

import { memo, useCallback, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { Input } from '@/components/ui/Input';
import { t } from '@/i18n';
import { useSessionActions } from '@/hooks/useSession';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import type { StepProps } from '@/types/steps';
import { getStepIcon } from '@/utils/stepIcons';
import styles from './AddressValidationStep.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GeocodedResult {
  latitude: number;
  longitude: number;
  confidence: number | null;
  provider: string | null;
  staticMapUrl: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse geocoded coordinates from options[0].metadata. */
function parseGeocoded(options: StepProps['options']): GeocodedResult | null {
  const first = options.options[0];
  const meta = first?.metadata;
  if (!meta || typeof meta['latitude'] !== 'number' || typeof meta['longitude'] !== 'number') {
    return null;
  }
  return {
    latitude: meta['latitude'],
    longitude: meta['longitude'],
    confidence: typeof meta['confidence'] === 'number' ? meta['confidence'] : null,
    provider: typeof meta['provider'] === 'string' ? meta['provider'] : null,
    staticMapUrl:
      typeof meta['static_map_url'] === 'string' ? meta['static_map_url'] : null,
  };
}

/** Format coordinate as degrees string: 59.3345°N */
function fmtCoord(value: number, pos: string, neg: string): string {
  const dir = value >= 0 ? pos : neg;
  return `${Math.abs(value).toFixed(6)}°${dir}`;
}

const isValidLat = (v: string): boolean => {
  const n = parseFloat(v);
  return !isNaN(n) && n >= -90 && n <= 90;
};

const isValidLon = (v: string): boolean => {
  const n = parseFloat(v);
  return !isNaN(n) && n >= -180 && n <= 180;
};

// ---------------------------------------------------------------------------
// Confidence badge
// ---------------------------------------------------------------------------

interface ConfidenceBadgeProps {
  value: number | null;
}

function ConfidenceBadge({ value }: ConfidenceBadgeProps) {
  let level: 'high' | 'medium' | 'low';
  let label: string;
  if (value !== null && value >= 0.8) {
    level = 'high';
    label = `${Math.round(value * 100)}%`;
  } else if (value !== null && value >= 0.5) {
    level = 'medium';
    label = `${Math.round(value * 100)}%`;
  } else {
    level = 'low';
    label = value !== null ? `${Math.round(value * 100)}%` : '—';
  }

  return (
    <span className={`${styles['confidence']} ${styles[`confidence-${level}`]}`} aria-label={`${t('steps.address_validation.confidence')}: ${label}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AddressValidationStep = memo(function AddressValidationStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const { goBack } = useSessionActions();
  const { demoMode } = useWidgetConfig();
  const rc = options.rule_context;
  const geocodingFailed = rc['geocoding_failed'] === true || rc['geocoding_failed'] === 'true';
  const geocoded = parseGeocoded(options);
  const address = typeof rc['original_address'] === 'string' ? rc['original_address'] : '';

  const [editing, setEditing] = useState(false);
  const [lat, setLat] = useState(geocoded ? String(geocoded.latitude) : '');
  const [lng, setLng] = useState(geocoded ? String(geocoded.longitude) : '');

  // ── Validation ──
  const needsCoords = geocodingFailed || editing;
  const coordsValid = isValidLat(lat) && isValidLon(lng);
  const canSubmit = needsCoords ? coordsValid : !!geocoded;

  // ── Submit ──
  const handleConfirm = useCallback(() => {
    if (!canSubmit) return;

    if (needsCoords) {
      onSubmit({
        confirmed: true,
        coordinates: {
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
        },
      });
    } else {
      onSubmit({ confirmed: true });
    }
  }, [canSubmit, needsCoords, lat, lng, onSubmit]);

  const handleGoBack = useCallback(() => {
    void goBack();
  }, [goBack]);

  // ── Heading ──
  const title = geocodingFailed
    ? t('steps.address_validation.failedTitle')
    : t('steps.address_validation.title');
  const subtitle = geocodingFailed
    ? t('steps.address_validation.failedSubtitle')
    : t('steps.address_validation.subtitle');

  return (
    <div className={styles['container']}>
      <StepHeading
        title={title}
        subtitle={subtitle}
        icon={getStepIcon(stepId)}
      />

      {/* ── Geocoding failed → warning + go back + manual entry ── */}
      {geocodingFailed && (
        <div className={styles['container']}>
          <div className={styles['warning']} role="alert">
            <span className={styles['warning-icon']} aria-hidden="true">⚠️</span>
            <p className={styles['warning-text']}>{t('steps.address_validation.geocodingFailed')}</p>
          </div>

          <button
            type="button"
            className={styles['go-back-link']}
            onClick={handleGoBack}
            disabled={isSubmitting}
          >
            {t('steps.address_validation.goBack')}
          </button>

          <div className={styles['form']}>
            <Input
              label={t('steps.address_validation.latitudeLabel')}
              name="latitude"
              type="number"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              error={lat && !isValidLat(lat) ? t('steps.address.latRange') : undefined}
              required
            />
            <Input
              label={t('steps.address_validation.longitudeLabel')}
              name="longitude"
              type="number"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              error={lng && !isValidLon(lng) ? t('steps.address.lonRange') : undefined}
              required
            />
          </div>
        </div>
      )}

      {/* ── Geocoding succeeded (low confidence) → confirmation card ── */}
      {!geocodingFailed && geocoded && (
        <div className={styles['container']}>
          <div className={styles['card']}>
            {/* Static map image */}
            {geocoded.staticMapUrl && (
              <img
                src={geocoded.staticMapUrl}
                alt={t('steps.address_validation.mapAlt')}
                loading="lazy"
                width={400}
                height={400}
                className={styles['map-preview']}
              />
            )}

            {/* Address + coordinates */}
            <div className={styles['card-body']}>
              {address && <p className={styles['address']}>{address}</p>}

              {/* Detected location name from options[0] */}
              {options.options[0]?.name && options.options[0].name !== address && (
                <p className={styles['detected']}>{options.options[0].name}</p>
              )}

              {/* Coordinates (demo mode only) */}
              {demoMode && (
                <p className={styles['coords-text']}>
                  {fmtCoord(geocoded.latitude, 'N', 'S')},{' '}
                  {fmtCoord(geocoded.longitude, 'E', 'W')}
                </p>
              )}

              {/* Confidence + provider (demo mode only) */}
              {demoMode && (
                <div className={styles['meta']}>
                  <ConfidenceBadge value={geocoded.confidence} />
                  {geocoded.provider && (
                    <span className={styles['provider']}>
                      {geocoded.provider}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Edit coordinates toggle (demo mode only) */}
          {demoMode && editing && (
            <div className={styles['form']}>
              <Input
                label={t('steps.address_validation.latitudeLabel')}
                name="latitude"
                type="number"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                error={lat && !isValidLat(lat) ? t('steps.address.latRange') : undefined}
                required
              />
              <Input
                label={t('steps.address_validation.longitudeLabel')}
                name="longitude"
                type="number"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                error={lng && !isValidLon(lng) ? t('steps.address.lonRange') : undefined}
                required
              />
            </div>
          )}
        </div>
      )}

      <FooterActions
        primaryLabel={t('steps.address_validation.confirm')}
        onPrimary={handleConfirm}
        isLoading={isSubmitting}
        disabled={!canSubmit}
        secondaryLabel={
          !demoMode
            ? undefined
            : geocodingFailed
              ? undefined
              : !geocoded
                ? undefined
                : !editing
                  ? t('steps.address_validation.editCoords')
                  : t('steps.address_validation.cancelEdit')
        }
        onSecondary={
          demoMode && !geocodingFailed && geocoded
            ? () => setEditing((prev) => !prev)
            : undefined
        }
      />
    </div>
  );
});
